/**
 * CTAButton — DeCarbonIQ Design System
 * Standardized action buttons.
 *
 * Variants:
 *   primary   — Navy bg, white text (default)
 *   action    — Teal/green bg, white text (primary CTA)
 *   secondary — White bg, slate border, dark text
 *   ghost     — No bg, subtle text
 *   danger    — Red bg, white text
 *
 * Sizes: sm | md (default) | lg
 */
import React from "react";
import { RefreshCw } from "lucide-react";

const VARIANTS = {
  primary:   "bg-slate-900 text-white border-transparent hover:bg-slate-800",
  action:    "bg-[#059669] text-white border-transparent hover:bg-emerald-700",
  secondary: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
  ghost:     "bg-transparent text-slate-600 border-transparent hover:bg-slate-100",
  danger:    "bg-red-600 text-white border-transparent hover:bg-red-700",
  outline:   "bg-transparent text-[#059669] border-[#059669]/40 hover:bg-emerald-50",
};

const SIZES = {
  sm:  "px-3 py-1.5 text-xs gap-1.5",
  md:  "px-4 py-2.5 text-sm gap-2",
  lg:  "px-6 py-3 text-sm gap-2",
};

export function CTAButton({
  variant = "primary",
  size = "md",
  icon: Icon,
  iconRight,
  loading = false,
  disabled,
  children,
  className = "",
  ...props
}) {
  const variantClass = VARIANTS[variant] || VARIANTS.primary;
  const sizeClass    = SIZES[size]    || SIZES.md;

  return (
    <button
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center font-medium rounded-xl border
        transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed
        ${variantClass} ${sizeClass} ${className}
      `}
      {...props}
    >
      {loading ? (
        <RefreshCw size={13} className="animate-spin flex-shrink-0" />
      ) : Icon ? (
        <Icon size={size === "sm" ? 12 : 14} className="flex-shrink-0" />
      ) : null}
      {children}
      {iconRight && !loading && (
        <iconRight size={size === "sm" ? 12 : 14} className="flex-shrink-0" />
      )}
    </button>
  );
}

export default CTAButton;
